<template>
  <div class="right_sidebar">
    <div class="navs_container">
      <div class="d-flex-row justify-start height-max-content">
        <BurgerIcon />
        <p class="t-light font-family-2 regular-l">
          {{ $t("blog.on_this_page") }}
        </p>
      </div>
      <div
        class="d-flex-column justify-start align-start gap-12 main_container"
        id="main_container"
      >
        <div
          class="context-h2-container"
          v-for="(h2el, index) in h2Content"
          :key="h2el.h2"
          @click="navToEl(h2el?.content, index)"
        >
          <a
            class="context-h2 font-family-2 regular-m nav-item"
            :class="{ active: activeSection === getElementId(h2el?.content) }"
            :id="`nav-${getElementId(h2el?.content)}`"
          >
            {{ getH2Title(h2el?.content) }}
          </a>
          <template v-if="h2el?.h3?.length > 0">
            <a
              class="context-h2 font-family-2 regular-m h3-nav nav-item"
              v-for="(h3, childIndex) in h2el?.h3"
              :key="h3?._key"
              @click.stop="navToEl(h3, childIndex)"
              :id="`nav-${getElementId(h3)}`"
              :class="{ active: activeSection === getElementId(h3) }"
            >
              {{ getH2Title(h3) }}
            </a>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import BurgerIcon from "../icons/blog/BurgerIcon.vue";
const props = defineProps({
  blog: {
    default: null,
  },
});

const activeSection = ref("");
const disableObserver = ref(false);
const observer = ref(null);
const scrollTimeout = ref(null);
const h2Content = computed(() => {
  if (!props.blog) return [];

  return generateTableOfContents(props.blog?.content);
});
function generateTableOfContents(sanityBlocks) {
  try {
    let h3Elements = [];

    sanityBlocks?.forEach((block, index) => {
      if (block.style === "h3") {
        h3Elements.push(index);
      }
    });

    const h2Elements = sanityBlocks
      .map((item, index) => {
        if (item.style === "h2") {
          return { h2: index, content: item, h3: [] }; // Include an empty h3 array
        }
        return null;
      })
      .filter(Boolean);

    // Populate h3 inside the correct h2
    for (let i = 0; i < h2Elements.length; i++) {
      const currentH2Index = h2Elements[i].h2;
      const nextH2Index = h2Elements[i + 1]?.h2; // Get next H2 index or undefined

      h2Elements[i].h3 = h3Elements
        .filter(
          (el) =>
            el > currentH2Index &&
            (nextH2Index === undefined || el < nextH2Index)
        )
        .map((el) => sanityBlocks[el]);
    }

    return h2Elements;
  } catch (error) {
    console.log(error);
    return [];
  }
}

function getH2Title(h2El) {
  const content = h2El?.children?.map((span) => span.text).join("") || "";
  return content;
}

const navToEl = (h2El, index) => {
  disableObserver.value = true; // Disable observer

  const elId = generateSlug(getH2Title(h2El));
  const el = document.getElementById(`link-${elId}`);
  const navEl = document.getElementById(`nav-${elId}`);
  activeSection.value = elId;

  handleActive(navEl);

  if (el) {
    el.scrollIntoView({
      block: "center",
      behavior: "smooth", // Smooth scrolling for better UX
    });

    setTimeout(() => {
      disableObserver.value = false;
      updateActiveSection(); // Ensure correct section is active
    }, 500); // Adjust delay as needed
  }
};

const makeElActive = (elId) => {
  const el = document.getElementById(`link-${elId}`);
  const navEl = document.getElementById(`nav-${elId}`);
  activeSection.value = elId;
  handleActive(navEl);
};

const handleActive = (el) => {
  const childs = Array.from(document.getElementsByClassName("nav-item"));
  const index = childs.indexOf(el);
  if (index < 0) return;
  const offsetValue = index * 28;
  document
    .getElementById("main_container")
    .style.setProperty("--indicator-offset", `${offsetValue}px`);
};
const generateSlug = (text) => {
  return text
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\w-]+/g, "");
};

const getElementId = (el) => {
  return generateSlug(getH2Title(el));
};

onNuxtReady(() => {
  attachObserver(); // Set up IntersectionObserver
  updateActiveSection(); // Ensure the correct section is active on load
});
const updateActiveSection = () => {
  let closestElement = null;
  let closestDistance = window.innerHeight;

  document.querySelectorAll("h2, h3").forEach((el) => {
    const rect = el.getBoundingClientRect();
    const elementCenter = rect.top + rect.height / 2;
    const viewportCenter = window.innerHeight / 2;
    const distance = Math.abs(viewportCenter - elementCenter);

    if (distance < closestDistance && rect.top >= 0) {
      closestDistance = distance;
      closestElement = el;
    }
  });

  if (closestElement) {
    const elId = closestElement.getAttribute("id").replace("link-", "");
    makeElActive(elId);
  }
};

const onScroll = () => {
  if (scrollTimeout.value) cancelAnimationFrame(scrollTimeout.value);
  scrollTimeout.value = requestAnimationFrame(() => {
    const scrollPosition = window.innerHeight + window.scrollY;
    const pageHeight = document.documentElement.scrollHeight;

    // Check if the user is at the bottom of the page
    if (scrollPosition >= pageHeight - 5) {
      const allHeadings = document.querySelectorAll("h2, h3");
      if (allHeadings.length > 0) {
        const lastHeading = allHeadings[allHeadings.length - 1];
        if (lastHeading) {
          const elId = lastHeading.getAttribute("id")?.replace("link-", "");
          makeElActive(elId);
        }
      }
    } else {
      updateActiveSection();
    }
  });
};

const attachObserver = () => {
  if (!process.client) return;

  observer.value = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) updateActiveSection();
      });
    },
    { root: null, rootMargin: "0px", threshold: 0.1 } // Lower threshold for faster updates
  );

  nextTick(() => {
    document
      .querySelectorAll("h2, h3")
      .forEach((el) => observer.value.observe(el));
    window.addEventListener("scroll", onScroll);
  });
};
onBeforeUnmount(() => {
  window.removeEventListener("scroll", onScroll);
  observer.value?.disconnect();
  activeSection.value = "";
});
</script>
<style lang="scss"></style>
